import { useState, useEffect, useRef, useCallback } from "react";
import { Play, Star, Clock, X, ChevronLeft, ChevronRight, Calendar, User, Film, Heart, Youtube, Plus, Check } from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import api from "@/services/api";
import { proxyImageUrl } from "@/utils/imageProxy";
import { isFavorite, toggleFavorite, getProgress } from "@/utils/watchData";
import { BannerHero } from "@/components/BannerHero";

function MovieCard({ movie, onClick, onFavToggle }) {
  const [fav, setFav] = useState(() => isFavorite(movie.stream_id));
  const progress = getProgress(movie.stream_id);

  const handleFav = (e) => {
    e.stopPropagation();
    const added = toggleFavorite({
      id: movie.stream_id,
      name: movie.name,
      poster: movie.stream_icon,
      type: 'vod',
      genre: movie.genre,
      rating: movie.rating,
      year: (movie.release_date || "").slice(0, 4),
    });
    setFav(added);
    if (onFavToggle) onFavToggle();
  };

  return (
    <div
      data-testid={`movie-card-${movie.stream_id}`}
      onClick={() => onClick(movie)}
      className="flex-shrink-0 w-[160px] group cursor-pointer"
    >
      <div className="relative aspect-[2/3] rounded-lg overflow-hidden bg-zinc-800/50 mb-2">
        {movie.stream_icon ? (
          <img
            src={proxyImageUrl(movie.stream_icon)}
            alt={movie.name}
            className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
            loading="lazy"
            onError={(e) => { e.target.style.display = "none"; }}
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <Film className="w-10 h-10 text-zinc-600" />
          </div>
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-200" />
        <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-200">
          <div className="w-11 h-11 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center">
            <Play className="w-5 h-5 text-white ml-0.5" fill="white" />
          </div>
        </div>
        {/* Favorite button */}
        <button
          data-testid={`fav-btn-${movie.stream_id}`}
          onClick={handleFav}
          className={`absolute top-2 left-2 w-7 h-7 rounded-full flex items-center justify-center transition-all ${fav ? 'bg-red-500/30' : 'bg-black/50 hover:bg-black/70'}`}
        >
          <Heart className={`w-3.5 h-3.5 ${fav ? 'text-red-500 fill-red-500' : 'text-white/70'}`} />
        </button>
        {movie.rating_5based > 0 && (
          <div className="absolute top-2 right-2 flex items-center gap-1 px-1.5 py-0.5 bg-black/60 backdrop-blur-sm rounded text-[10px] font-medium text-yellow-400">
            <Star className="w-2.5 h-2.5" fill="currentColor" />
            {movie.rating_5based}
          </div>
        )}
        {/* Progress bar */}
        {progress && (
          <div className="absolute bottom-0 left-0 right-0 h-[3px] bg-zinc-700">
            <div className="h-full bg-red-500" style={{ width: `${progress.percent}%` }} />
          </div>
        )}
      </div>
      <p className="text-sm font-medium truncate text-zinc-200 group-hover:text-white transition-colors">{movie.name}</p>
      {movie.release_date && (
        <p className="text-[11px] text-zinc-500">{movie.release_date.slice(0, 4)}</p>
      )}
    </div>
  );
}

function ScrollRow({ title, movies, onMovieClick }) {
  const scrollRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const checkScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 10);
    setCanScrollRight(el.scrollLeft < el.scrollWidth - el.clientWidth - 10);
  }, []);

  useEffect(() => {
    checkScroll();
    const el = scrollRef.current;
    if (el) el.addEventListener("scroll", checkScroll);
    return () => el?.removeEventListener("scroll", checkScroll);
  }, [checkScroll, movies]);

  const scroll = (dir) => {
    scrollRef.current?.scrollBy({ left: dir * 400, behavior: "smooth" });
  };

  if (!movies.length) return null;

  return (
    <div className="relative group/row">
      <h2 className="text-lg font-semibold mb-3 text-zinc-100">{title}</h2>
      <div className="relative">
        {canScrollLeft && (
          <button
            onClick={() => scroll(-1)}
            className="absolute left-0 top-0 bottom-0 z-10 w-10 flex items-center justify-center bg-gradient-to-r from-[#09090b] to-transparent opacity-0 group-hover/row:opacity-100 transition-opacity"
          >
            <ChevronLeft className="w-6 h-6" />
          </button>
        )}
        <div
          ref={scrollRef}
          className="flex gap-3 overflow-x-auto pb-2"
          style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
        >
          {movies.map((m) => (
            <MovieCard key={m.stream_id} movie={m} onClick={onMovieClick} />
          ))}
        </div>
        {canScrollRight && (
          <button
            onClick={() => scroll(1)}
            className="absolute right-0 top-0 bottom-0 z-10 w-10 flex items-center justify-center bg-gradient-to-l from-[#09090b] to-transparent opacity-0 group-hover/row:opacity-100 transition-opacity"
          >
            <ChevronRight className="w-6 h-6" />
          </button>
        )}
      </div>
    </div>
  );
}

export function MovieDetailModal({ movie, onClose, onPlay }) {
  const [details, setDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [fav, setFav] = useState(() => isFavorite(movie?.stream_id));
  const [showTrailer, setShowTrailer] = useState(false);

  useEffect(() => {
    if (!movie) return;
    let cancelled = false;
    setLoading(true);
    api.getVodDetails(movie.stream_id).then((data) => {
      if (!cancelled) {
        setDetails(data);
        setLoading(false);
      }
    }).catch(() => {
      if (!cancelled) setLoading(false);
    });
    return () => { cancelled = true; };
  }, [movie]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  if (!movie) return null;

  const info = details?.info || {};
  const backdrop = info.backdrop || info.backdrop_path?.[0];
  const poster = info.movie_image || movie.stream_icon;

  return (
    <div
      data-testid="movie-detail-modal"
      className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto"
      onClick={onClose}
    >
      <div className="fixed inset-0 bg-black/80 backdrop-blur-sm" />
      <div
        className="relative w-full max-w-3xl my-8 mx-4 bg-zinc-900 rounded-xl overflow-hidden shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Backdrop */}
        <div className="relative h-[320px] overflow-hidden">
          {backdrop ? (
            <img src={proxyImageUrl(backdrop)} alt="" className="w-full h-full object-cover" />
          ) : poster ? (
            <img src={proxyImageUrl(poster)} alt="" className="w-full h-full object-cover blur-sm scale-110" />
          ) : (
            <div className="w-full h-full bg-zinc-800" />
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-zinc-900 via-zinc-900/40 to-transparent" />

          {/* Close button */}
          <button
            data-testid="modal-close-btn"
            onClick={onClose}
            className="absolute top-4 right-4 w-9 h-9 rounded-full bg-zinc-900/70 flex items-center justify-center hover:bg-zinc-800 transition-colors z-10"
          >
            <X className="w-4 h-4" />
          </button>

          {/* Bottom info overlay */}
          <div className="absolute bottom-0 left-0 right-0 p-6 flex items-end gap-5">
            {poster && (
              <img
                src={proxyImageUrl(poster)}
                alt={movie.name}
                className="w-28 h-auto rounded-lg shadow-lg flex-shrink-0 border border-zinc-700/40"
              />
            )}
            <div className="flex-1 min-w-0">
              <h2 className="text-2xl font-bold mb-2 leading-tight">{movie.name}</h2>
              <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-zinc-400">
                {movie.release_date && (
                  <span className="flex items-center gap-1">
                    <Calendar className="w-3.5 h-3.5" />
                    {movie.release_date.slice(0, 4)}
                  </span>
                )}
                {info.duration && (
                  <span className="flex items-center gap-1">
                    <Clock className="w-3.5 h-3.5" />
                    {info.duration}
                  </span>
                )}
                {(movie.rating || info.rating) && (
                  <span className="flex items-center gap-1 text-yellow-400">
                    <Star className="w-3.5 h-3.5" fill="currentColor" />
                    {parseFloat(movie.rating || info.rating).toFixed(1)}
                  </span>
                )}
              </div>
              {(movie.genre || info.genre) && (
                <p className="text-xs text-zinc-500 mt-1.5">{movie.genre || info.genre}</p>
              )}
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 space-y-5">
          {/* Action buttons */}
          <div className="flex gap-2.5">
            <button
              data-testid="modal-play-btn"
              onClick={() => onPlay(movie)}
              className="flex-1 flex items-center justify-center gap-2 py-3 bg-blue-600 hover:bg-blue-500 rounded-lg font-semibold transition-colors"
            >
              <Play className="w-5 h-5" fill="white" />
              Oynat
            </button>
            {!loading && info.youtube_trailer && (
              <button
                data-testid="modal-trailer-btn"
                onClick={() => setShowTrailer(!showTrailer)}
                className={`flex items-center justify-center gap-2 px-5 py-3 rounded-lg font-semibold transition-colors ${showTrailer ? 'bg-red-600 hover:bg-red-500' : 'bg-zinc-800 hover:bg-zinc-700'}`}
              >
                <Youtube className="w-5 h-5" />
                Trailer
              </button>
            )}
            <button
              data-testid="modal-fav-btn"
              onClick={() => {
                const added = toggleFavorite({
                  id: movie.stream_id,
                  name: movie.name,
                  poster: movie.stream_icon,
                  type: 'vod',
                  genre: movie.genre,
                  rating: movie.rating,
                  year: (movie.release_date || "").slice(0, 4),
                });
                setFav(added);
              }}
              className={`flex items-center justify-center gap-2 w-12 sm:w-44 px-0 sm:px-4 py-3 rounded-lg font-medium transition-colors text-sm flex-shrink-0 ${fav ? 'bg-red-500/20 hover:bg-red-500/30 text-red-400' : 'bg-zinc-800 hover:bg-zinc-700 text-zinc-400'}`}
            >
              {fav ? <Check className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
              <span className="hidden sm:inline">{fav ? 'Siyahıda' : 'Siyahıya əlavə et'}</span>
            </button>
          </div>

          {/* YouTube Trailer Embed */}
          {showTrailer && info.youtube_trailer && (
            <div data-testid="trailer-embed" className="relative w-full aspect-video rounded-lg overflow-hidden bg-black">
              <iframe
                src={`https://www.youtube.com/embed/${info.youtube_trailer}?autoplay=1&rel=0`}
                title="Trailer"
                className="absolute inset-0 w-full h-full"
                allow="autoplay; encrypted-media"
                allowFullScreen
              />
            </div>
          )}

          {loading ? (
            <div className="flex justify-center py-4">
              <div className="w-6 h-6 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : (
            <>
              {info.plot && (
                <div>
                  <h3 className="text-sm font-semibold text-zinc-300 mb-1.5">Haqqında</h3>
                  <p className="text-sm text-zinc-400 leading-relaxed">{info.plot}</p>
                </div>
              )}

              {info.director && (
                <div className="flex items-start gap-2 text-sm">
                  <span className="text-zinc-500 flex-shrink-0">Rejissor:</span>
                  <span className="text-zinc-300">{info.director}</span>
                </div>
              )}

              {info.cast && (
                <div>
                  <h3 className="text-sm font-semibold text-zinc-300 mb-1.5 flex items-center gap-1.5">
                    <User className="w-3.5 h-3.5" /> Aktyor heyəti
                  </h3>
                  <p className="text-sm text-zinc-400 leading-relaxed">
                    {info.cast.split(",").slice(0, 8).join(", ")}
                  </p>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default function MoviesPage() {
  const [movies, setMovies] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [heroMovie, setHeroMovie] = useState(null);
  const [heroDetails, setHeroDetails] = useState(null);
  const [selectedMovie, setSelectedMovie] = useState(null);
  const [heroFav, setHeroFav] = useState(false);
  const [serverBanners, setServerBanners] = useState([]);
  const navigate = useNavigate();
  const location = useLocation();

  // Fetch categories and all movies
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [catData, movData] = await Promise.all([
          api.getVodCategories(),
          api.getVodContent({ per_page: 100, order_by: 'addtime_desc' }),
        ]);
        const cats = catData.content || [];
        const movs = movData.content || [];
        setCategories(cats);
        setMovies(movs);

        // Server banners come first in rotation
        let banners = [];
        try {
          const resp = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/banners?location=movies`);
          banners = resp.data.banners || [];
        } catch { /* fallback */ }
        setServerBanners(banners);

        // Pick random hero movie (shown after banners, or alone as fallback)
        if (movs.length > 0) {
          const hero = movs[Math.floor(Math.random() * movs.length)];
          setHeroMovie(hero);
          setHeroFav(isFavorite(hero.stream_id));
          // Fetch hero details for backdrop
          api.getVodDetails(hero.stream_id).then((d) => setHeroDetails(d.info || {})).catch(() => {});
        }
      } catch (err) {
        console.error("Failed to fetch movies:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const playMovie = (movie) => {
    navigate(`/app/player/vod/${movie.stream_id}`, { state: { returnPath: location.pathname } });
  };

  const openDetail = (movie) => {
    setSelectedMovie(movie);
  };

  // Group movies by category
  const moviesByCategory = categories.map((cat) => ({
    ...cat,
    movies: movies.filter((m) => m.categories?.includes(parseInt(cat.category_id))),
  })).filter((c) => c.movies.length > 0);

  // Filtered movies for "All" view
  const filteredMovies = selectedCategory
    ? movies.filter((m) => m.categories?.includes(parseInt(selectedCategory)))
    : movies;

  if (loading) {
    return (
      <div data-testid="movies-loading" className="flex items-center justify-center h-96">
        <div className="flex flex-col items-center gap-3">
          <div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
          <span className="text-sm text-zinc-500">Filmlər yüklənir...</span>
        </div>
      </div>
    );
  }

  const heroBackdrop = heroDetails?.backdrop || heroDetails?.backdrop_path?.[0];

  // Mixed rotation: server banners first, then the random movie hero as an extra slide
  const movieSlideImage = heroBackdrop ? proxyImageUrl(heroBackdrop) : (heroMovie?.stream_icon ? proxyImageUrl(heroMovie.stream_icon) : null);
  const combinedSlides = serverBanners.length > 0
    ? [
        ...serverBanners,
        ...(heroMovie && movieSlideImage
          ? [{
              id: `content-${heroMovie.stream_id}`,
              title: heroMovie.name,
              description: heroDetails?.plot || "",
              image_url: movieSlideImage,
              link_type: "vod",
              link_value: heroMovie.stream_id,
            }]
          : []),
      ]
    : [];

  return (
    <div data-testid="movies-page" className="space-y-8">
      {/* Server Banner Hero (admin-managed) or fallback movie hero */}
      {serverBanners.length > 0 ? (
        <BannerHero banners={combinedSlides} />
      ) : heroMovie && (
        <div data-testid="hero-banner" className="relative h-[280px] sm:h-[360px] md:h-[420px] overflow-hidden">
          {heroBackdrop ? (
            <img src={proxyImageUrl(heroBackdrop)} alt="" className="absolute inset-0 w-full h-full object-cover" />
          ) : heroMovie.stream_icon ? (
            <img src={proxyImageUrl(heroMovie.stream_icon)} alt="" className="absolute inset-0 w-full h-full object-cover blur-md scale-110 opacity-60" />
          ) : null}
          <div className="absolute inset-0 bg-gradient-to-r from-[#09090b] via-[#09090b]/70 to-transparent" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#09090b] via-transparent to-[#09090b]/30" />

          <div className="relative z-10 h-full flex flex-col justify-end p-5 sm:p-6 md:p-8 max-w-xl">
            <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-2 leading-tight">{heroMovie.name}</h1>
            <div className="flex items-center gap-3 text-sm text-zinc-400 mb-2">
              {heroMovie.release_date && <span>{heroMovie.release_date.slice(0, 4)}</span>}
              {heroDetails?.duration && <span>{heroDetails.duration}</span>}
              {heroMovie.rating && (
                <span className="flex items-center gap-1 text-yellow-400">
                  <Star className="w-3.5 h-3.5" fill="currentColor" />
                  {parseFloat(heroMovie.rating).toFixed(1)}
                </span>
              )}
            </div>
            {heroMovie.genre && (
              <p className="text-sm text-zinc-500 mb-3">{heroMovie.genre}</p>
            )}
            {heroDetails?.plot && (
              <p className="text-sm text-zinc-400 mb-5 line-clamp-2">{heroDetails.plot}</p>
            )}
            <div className="flex gap-3">
              <button
                data-testid="hero-play-btn"
                onClick={() => playMovie(heroMovie)}
                className="flex items-center gap-2 px-6 py-2.5 bg-white text-black font-semibold rounded-lg hover:bg-zinc-200 transition-colors text-sm"
              >
                <Play className="w-4 h-4" fill="currentColor" />
                Oynat
              </button>
              <button
                data-testid="hero-info-btn"
                onClick={() => openDetail(heroMovie)}
                className="flex items-center gap-2 px-6 py-2.5 bg-zinc-700/60 backdrop-blur-sm font-semibold rounded-lg hover:bg-zinc-600/60 transition-colors text-sm"
              >
                Ətraflı
              </button>
              <button
                data-testid="hero-fav-btn"
                onClick={() => {
                  const added = toggleFavorite({
                    id: heroMovie.stream_id,
                    name: heroMovie.name,
                    poster: heroMovie.stream_icon,
                    type: 'vod',
                    genre: heroMovie.genre,
                    rating: heroMovie.rating,
                    year: (heroMovie.release_date || "").slice(0, 4),
                  });
                  setHeroFav(added);
                }}
                className={`flex items-center gap-2 px-5 py-2.5 backdrop-blur-sm font-semibold rounded-lg transition-colors text-sm ${heroFav ? 'bg-red-500/20 hover:bg-red-500/30 text-red-400' : 'bg-zinc-700/60 hover:bg-zinc-600/60'}`}
              >
                {heroFav ? <Check className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
                {heroFav ? 'Siyahıda' : 'Siyahıya əlavə et'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Content */}
      <div className="px-4 sm:px-6 space-y-8">
        {/* Category filter pills */}
        <div data-testid="category-filter" className="flex items-center gap-2 flex-wrap">
          <button
            onClick={() => setSelectedCategory(null)}
            className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors ${
              !selectedCategory
                ? "bg-white text-black"
                : "bg-zinc-800 text-zinc-400 hover:bg-zinc-700 hover:text-white"
            }`}
          >
            Hamısı
          </button>
          {categories.map((cat) => (
            <button
              key={cat.category_id}
              onClick={() => setSelectedCategory(
                selectedCategory === cat.category_id ? null : cat.category_id
              )}
              className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors ${
                selectedCategory === cat.category_id
                  ? "bg-white text-black"
                  : "bg-zinc-800 text-zinc-400 hover:bg-zinc-700 hover:text-white"
              }`}
            >
              {cat.category_name}
            </button>
          ))}
        </div>

        {/* Category rows or filtered grid */}
        {selectedCategory ? (
          /* Grid view when category selected */
          <div data-testid="movies-grid" className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 2xl:grid-cols-7 gap-3 sm:gap-4">
            {filteredMovies.map((m) => (
              <MovieCard key={m.stream_id} movie={m} onClick={openDetail} />
            ))}
          </div>
        ) : (
          /* Row view for all categories */
          <div className="space-y-7">
            {/* All movies row — always on top */}
            <ScrollRow
              title="Bütün Filmlər"
              movies={movies}
              onMovieClick={openDetail}
            />
            {moviesByCategory.map((cat) => (
              <ScrollRow
                key={cat.category_id}
                title={cat.category_name}
                movies={cat.movies}
                onMovieClick={openDetail}
              />
            ))}
          </div>
        )}
      </div>

      {/* Movie Detail Modal */}
      {selectedMovie && (
        <MovieDetailModal
          movie={selectedMovie}
          onClose={() => setSelectedMovie(null)}
          onPlay={playMovie}
        />
      )}
    </div>
  );
}
